import { useEffect, useRef, useState } from 'react'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface TweenOptions {
    active?: boolean
    duration?: number
    delay?: number
    from?: number
}

const easeOutQuart = (t: number): number => 1 - Math.pow(1 - t, 4)

/**
 * Smoothly animates a number towards `target`. Starts at `from` and, once
 * active, glides to every new target from wherever it currently is.
 */
export const useTween = (target: number, { active = true, duration = 1400, delay = 0, from = 0 }: TweenOptions = {}): number => {
    const reduced = useReducedMotion()
    const [value, setValue] = useState(from)
    const valueRef = useRef(from)
    const firstRun = useRef(true)

    useEffect(() => {
        if (!active) return

        if (reduced) {
            valueRef.current = target
            setValue(target)
            return
        }

        const start = valueRef.current
        const delta = target - start

        if (delta === 0) return

        const wait = firstRun.current ? delay : 0
        firstRun.current = false

        let frame = 0
        let startedAt: number | null = null

        const step = (now: number) => {
            if (startedAt === null) startedAt = now + wait

            const elapsed = now - startedAt

            if (elapsed < 0) {
                frame = requestAnimationFrame(step)
                return
            }

            const progress = Math.min(1, elapsed / duration)
            const next = start + delta * easeOutQuart(progress)

            valueRef.current = next
            setValue(next)

            if (progress < 1) frame = requestAnimationFrame(step)
        }

        frame = requestAnimationFrame(step)

        return () => cancelAnimationFrame(frame)
    }, [target, active, duration, delay, reduced])

    return value
}
